// Web push registration for the browser. Registers the service worker
// (public/sw.js), subscribes to the Push API using the server's VAPID public
// key, and sends the subscription to the API so the backend can push to this
// browser (see api/src/webpush.js + api/src/functions/webPushSubscriptions.js).
//
// Best-effort throughout: any failure (unsupported browser, denied permission,
// web push not configured on the server) is logged and swallowed — push is an
// optional enhancement and must never block the app.

import { api } from "../api/client";

// Base64url → Uint8Array, the format PushManager.subscribe wants for
// applicationServerKey.
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

let registration = null;

async function ensureServiceWorker() {
  if (registration) return registration;
  registration = await navigator.serviceWorker.register("/sw.js");
  return registration;
}

/**
 * Register this browser for web push. Call after sign-in. Prompts for the
 * notification permission the first time. No-op (resolves) when unsupported,
 * denied, or the server hasn't configured VAPID keys.
 */
export async function registerWebPush() {
  try {
    if (!pushSupported()) return;

    // Ask the server for the VAPID public key. Null → web push not configured;
    // skip silently rather than prompting the user for nothing.
    const { publicKey } = await api.get("webpush/public-key");
    if (!publicKey) return;

    // Only prompt if the user hasn't already decided. A prior "denied" stays
    // denied until they change it in browser settings — don't nag.
    let permission = Notification.permission;
    if (permission === "default") permission = await Notification.requestPermission();
    if (permission !== "granted") return;

    const reg = await ensureServiceWorker();
    await navigator.serviceWorker.ready;

    // Reuse an existing subscription if present; otherwise create one.
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }

    await api.post("webpush/subscribe", sub.toJSON());
  } catch (err) {
    console.warn("[webpush] register failed:", err);
  }
}

/** Unsubscribe this browser (best-effort) — call on sign-out. */
export async function unregisterWebPush() {
  try {
    if (!pushSupported()) return;
    const reg = registration || (await navigator.serviceWorker.getRegistration("/sw.js"));
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    await api.post("webpush/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
    await sub.unsubscribe().catch(() => {});
  } catch (err) {
    console.warn("[webpush] unregister failed:", err);
  }
}
